/**
 * Changing a person's Agent instructions one named piece at a time.
 *
 * An agent never sends the whole text back. It names the exact words it
 * replaces, so it cannot drop or reword a line it was not asked about, and a
 * change made on the phone in the meantime is never erased: if the words the
 * agent names are gone, the change is refused and the agent reads again.
 *
 * Pure and free of Deno imports so Vitest can import it directly.
 */

import { AgentError } from "./contract.ts";

/** The same limit the database enforces on the stored text. */
export const MAX_INSTRUCTIONS_LENGTH = 20_000;

export interface InstructionsChange {
  /** The exact words in the instructions now. Empty adds new_text at the end. */
  old_text: string;
  /** What replaces them. Empty removes them. */
  new_text: string;
}

const READ_AGAIN =
  "Nothing was saved. Read them again with my_instructions and use the words exactly as they are now.";

/** Validates the `changes` an agent sent. */
export function readChanges(input: Record<string, unknown>): InstructionsChange[] {
  const raw = input.changes;
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new AgentError(
      "changes must list at least one change, each with old_text (the exact words in the instructions now, or empty to add at the end) and new_text (what replaces them, or empty to remove them).",
    );
  }
  return raw.map((entry, index) => {
    const change = (entry ?? {}) as Record<string, unknown>;
    const oldText = change.old_text ?? "";
    const newText = change.new_text ?? "";
    if (typeof oldText !== "string" || typeof newText !== "string") {
      throw new AgentError(`Change ${index + 1}: old_text and new_text must be text.`);
    }
    if (!oldText && !newText.trim()) {
      throw new AgentError(
        `Change ${index + 1} is empty: give the words to add as new_text, or the words to change as old_text.`,
      );
    }
    return { old_text: oldText, new_text: newText };
  });
}

/**
 * Counts every place the text starts, overlapping ones too: "aa" is in "aaa"
 * twice, so a change naming it cannot say which one it means.
 */
function occurrences(body: string, text: string): number {
  let count = 0;
  let from = body.indexOf(text);
  while (from !== -1) {
    count += 1;
    from = body.indexOf(text, from + 1);
  }
  return count;
}

/**
 * Length as the database counts it (`char_length`, one per code point), not
 * JavaScript's UTF-16 units, so text the database accepts is never refused
 * here for being too long.
 */
function characterCount(text: string): number {
  return Array.from(text).length;
}

/** Adds text as a line of its own after whatever is there. */
function appendLine(body: string, text: string): string {
  const addition = text.trim();
  // trimEnd, not /\s+$/: that pattern backtracks quadratically over long
  // runs of whitespace.
  const kept = body.trimEnd();
  return kept ? `${kept}\n${addition}` : addition;
}

/**
 * Removes the words, and the line break after them when they were a whole
 * line, so removing a rule does not leave a blank line where it was.
 */
function removeText(body: string, text: string): string {
  const at = body.indexOf(text);
  const end = at + text.length;
  const startsLine = at === 0 || body[at - 1] === "\n";
  if (startsLine && body[end] === "\n") {
    return body.slice(0, at) + body.slice(end + 1);
  }
  if (startsLine && end === body.length && at > 0) {
    return body.slice(0, at - 1);
  }
  return body.slice(0, at) + body.slice(end);
}

/**
 * Applies the changes in order. Each old_text must appear exactly once in
 * the text as the earlier changes left it.
 */
export function applyChanges(body: string, changes: InstructionsChange[]): string {
  let next = body;
  changes.forEach((change, index) => {
    const label = changes.length > 1 ? `Change ${index + 1}: ` : "";
    if (!change.old_text) {
      next = appendLine(next, change.new_text);
      return;
    }
    const count = occurrences(next, change.old_text);
    if (count === 0) {
      throw new AgentError(
        `${label}"${change.old_text}" is not in this person's Agent instructions any more — they may have changed since you read them. ${READ_AGAIN}`,
        409,
      );
    }
    if (count > 1) {
      throw new AgentError(
        `${label}"${change.old_text}" appears ${count} times in the Agent instructions, so JuDI cannot tell which one to change. Nothing was saved. Include more of the words around it.`,
        409,
      );
    }
    next = change.new_text
      ? next.replace(change.old_text, () => change.new_text)
      : removeText(next, change.old_text);
  });

  // A code point is one or two UTF-16 units, so a string within the limit in
  // units is within it in code points too, and needs no count.
  const length =
    next.length > MAX_INSTRUCTIONS_LENGTH ? characterCount(next) : next.length;
  if (length > MAX_INSTRUCTIONS_LENGTH) {
    throw new AgentError(
      `That would make the Agent instructions ${length} characters long, and the limit is ${MAX_INSTRUCTIONS_LENGTH}. Nothing was saved. Suggest to the person what could be shorter or removed.`,
    );
  }
  return next;
}
